import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import * as FileSaver from "file-saver";
import moment from "moment";
import { useDispatch, useSelector } from "react-redux";
import { useHistory, useLocation } from "react-router-dom";
import * as XLSX from "xlsx";
import {
  swalErrorMessage,
  swalWarningMessage,
} from "../../../Constant/swalMessage";
import {
  getCallStatusData,
  isEmpty,
  PAGE_SIZE_50,
} from "../CommonComponents/Common";
import {
  getCallHistoryAction,
  getCallHistoryCountAction,
} from "../ProjectSection/CampaignSection/Store/Campaign.action";
import CallHistoryHeader from "./CallHistoryHeader";
import { ReactComponent as RedirectIcon } from "../../../assets/icons/external-link.svg";
import chroma from "chroma-js";
import CallTranscriptModal from "./CallTranscriptModal";
import DynamicTable from "../CommonComponents/Table/DynamicTable";
import CallHistoryPopover from "./CallHistoryPopover";
import CallLogs from "../Campaigns/CallHistory/CallLogs";
import FilterBody from "../../../common/components/Filter/FilterBody";
import ReactAudioPlayer from "react-audio-player";
import CommonPopup from "../CommonComponents/PopUpCards/CommonPopup";
import { ReactComponent as ExportFileIcon } from "../../../assets/Images/ExportPopupIcon.svg";
import StatusButton from "../Common/Buttons/StatusButton";
import BaseButton from "../Common/Buttons/BaseButton";
import { shortenString } from "../../../Constant/helper";
import moment1 from "moment-timezone";
import "../../../assets/css/page/CallHistory.scss";
import { getDecryptedItem } from "../../../utils/localStorageHelper";
import { Bot, MoveLeft } from "lucide-react";
import { User } from "lucide-react";

const CallDirectionIcon = ({ callType }) => {
  const type = callType?.toUpperCase();
  const typeColorMap = {
    OUTBOUND: "#ffad1f",
    INBOUND: "#56c819",
  };

  const baseColor = typeColorMap[type];
  const bgColor = chroma(baseColor).alpha(0.3).hex();
  const rotation = type === "OUTBOUND" ? 135 : -45;

  return (
    <Tooltip title={type}>
      <Box
        className="flex-xy-center"
        sx={{
          width: 27,
          height: 27,
          borderRadius: "50%",
          backgroundColor: bgColor,
        }}
      >
        <MoveLeft
          size={16}
          style={{
            color: baseColor,
            transform: `rotate(${rotation}deg)`,
          }}
        />
      </Box>
    </Tooltip>
  );
};

const CallHistory = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const currentDate = moment().format("YYYY-MM-DD");
  const location = useLocation();
  const locSearch = new URLSearchParams(location.search);
  let Stalker = locSearch.get("stalker");
  let trunk = locSearch.get("trunk");
  const timezone = moment1.tz.guess();

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [showCallTranscriptModal, setShowCallTranscriptModal] = useState(false);
  const [selectedTranscript, setSelectedTranscript] = useState(null);
  const [searchText, setSearchText] = useState(undefined);
  const [isReady, setIsReady] = useState(false);
  const [filterName, setFilterName] = useState("");
  const [filterNameFromDropdown, setFilterNameFromDropdown] = useState("");
  const [dateRange, setDateRange] = useState([
    new Date(currentDate),
    new Date(currentDate),
  ]);
  const [exportLoad, setExportLoad] = useState(false);
  const [startDate, endDate] = dateRange;

  const reducer: any = useSelector((state) => state);
  const {
    AccountReducer: { constantData, selectedAccount },
    CampaignReducer: { callHistoryDetails, callHistoryDetailsTotalCounts },
  } = reducer;

  const [call_uuid, setCall_uuid] = useState("");
  const [show, setShow] = useState(false);
  const [searchTerm, setSearchTerm] = useState(
    getDecryptedItem("callHistorySearchTerm") || ""
  );
  const [exportPopup, setExportPopup] = useState(false);

  const [state, setState] = useState<any>({
    filterDrawer: false,
    trackEnabledRecording: [],
    pageIndex: currentPage,
    durationFilter: "",
    statusFilter: [],
    outComefilter: [],
    min: "",
    max: "",
    selectedProjectId: "",
    campaignDataList: [],
  });

  useEffect(() => {
    const appliedFilters = getDecryptedItem("callHistoryFilters") || {};
    const appliedFiltersLoc = appliedFilters?.[selectedAccount?._id];

    setFilterName(appliedFiltersLoc?.filterName || "");
    setDateRange([
      appliedFiltersLoc?.startDate
        ? new Date(appliedFiltersLoc?.startDate)
        : new Date(currentDate),
      appliedFiltersLoc?.endDate
        ? new Date(appliedFiltersLoc?.endDate)
        : new Date(currentDate),
    ]);

    setState((prevState) => ({
      ...prevState,
      durationFilter: appliedFiltersLoc?.durationFilter || "",
      outComefilter: appliedFiltersLoc?.outcomeFilters || [],
      statusFilter: appliedFiltersLoc?.statusFilter || [],
      min: appliedFiltersLoc?.customFilter
        ? appliedFiltersLoc?.customFilter.split("-")[0] || ""
        : "",
      max: appliedFiltersLoc?.customFilter
        ? appliedFiltersLoc?.customFilter.split("-")[1] || ""
        : "",
      selectedProjectId: appliedFiltersLoc?.selectedProjectId || "",
    }));
  }, [filterNameFromDropdown]);

  useEffect(() => {
    if (callHistoryDetails?.length === 0) setLoading(true);
    if (isReady) fetchCallHistory();
  }, [currentPage, searchText, isReady]);

  useEffect(() => {
    if (callHistoryDetailsTotalCounts) {
      setTotalPages(Math.ceil(callHistoryDetailsTotalCounts / PAGE_SIZE_50));
    }
  }, [currentPage, searchText, callHistoryDetailsTotalCounts]);

  useEffect(() => {
    if (callHistoryDetails) {
      setState((prev) => ({
        ...prev,
        campaignDataList: callHistoryDetails?.map((item) => item?.combinedData),
      }));
    }
  }, [callHistoryDetails]);

  const {
    filterDrawer,
    trackEnabledRecording,
    durationFilter,
    statusFilter,
    outComefilter,
    selectedProjectId,
    campaignDataList,
  } = state;

  const fetchCallHistory = async () => {
    const obj = {
      pageIndex: currentPage,
      pageSize: PAGE_SIZE_50,
      live: false,
      duration:
        durationFilter === "Custom"
          ? `${state?.min}-${state?.max}`
          : durationFilter?.split(" ")[0],
      searchText: searchText,
      from: moment(startDate).format("YYYY-MM-DD"),
      to: moment(endDate).format("YYYY-MM-DD"),
      projectId: selectedProjectId ? [selectedProjectId] : "",
      callStatus: statusFilter,
      outcome: outComefilter,
      timezone,
    };

    await dispatch(getCallHistoryAction(obj));
    setLoading(false);
    await dispatch(getCallHistoryCountAction(obj));
  };

  const applyRefresh = () => {
    setLoading(true);
    fetchCallHistory();
  };

  const openCallTranscriptModal = (transcriptData) => {
    setSelectedTranscript(transcriptData);
    setShowCallTranscriptModal(true);
  };

  const closeCallTranscriptModal = () => {
    setShowCallTranscriptModal(false);
    setSelectedTranscript(null);
  };

  const handleRedirect = (callData) => {
    if (callData?.isDeleted) {
      swalWarningMessage("Project is Deleted.");
      return;
    } else if (callData?.campaignDeleted) {
      swalWarningMessage("Campaign is deleted.");
      return;
    } else {
      history.push(
        `/account/project/${callData?.projectId}/campaign-runs?campaignid=${callData?.campaignId}&campdate=${callData?.createdAt}`
      );
    }
  };

  const exportToCSV = async () => {
    setExportLoad(true);
    let obj = {
      pageIndex: 1,
      pageSize: 1000,
      live: false,
      duration:
        durationFilter === "Custom"
          ? `${state?.min}-${state?.max}`
          : durationFilter?.split(" ")[0],
      searchText: searchText,
      from: moment(startDate).format("YYYY-MM-DD"),
      to: moment(endDate).format("YYYY-MM-DD"),
      projectId: selectedProjectId ? [selectedProjectId] : "",
      callStatus: statusFilter,
      outcome: outComefilter,
      timezone,
    };
    let res: any = await dispatch(getCallHistoryAction({ ...obj, all: true }));
    if (res?.length <= 0) {
      swalErrorMessage("Nothing to export.");
      setExportLoad(false);
      return;
    }
    let matchArr = [
      "mandatory_params",
      "firstName",
      "lastName",
      "FirstName",
      "LastName",
      "phone",
      "PhoneNum",
    ];

    if (res) {
      let arr: any = [];
      res?.forEach((kl) => {
        let callHistory = kl?.combinedData?.campaignRunDetails;
        let copy = callHistory?.customParameters;
        let obj: any = {};
        obj["First Name"] = copy?.FirstName || "--";
        obj["Last Name"] = copy?.LastName || "--";
        obj["Phone number"] = callHistory?.phone || "--";
        obj["Call UUID"] = callHistory?.call_uuid || "";
        obj["Calling Status"] =
          constantData?.callStatus[callHistory?.callStatus] || "";
        obj["Cost"] = callHistory?.newCost || "";
        if (selectedAccount?.params?.pricesWaivers) {
          obj["Cost"] = "--";
        }
        obj["Recording Url"] = callHistory?.callRecordingUrl || "";
        if (!isEmpty(copy)) {
          for (const customKeys in copy) {
            if (!matchArr.includes(customKeys)) {
              obj[customKeys] = copy[customKeys];
            }
          }
        }
        arr.push(obj);
      });
      const fileType =
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
      const fileExtension = ".xlsx";
      const ws = XLSX.utils.json_to_sheet(arr);
      const wb = { Sheets: { data: ws }, SheetNames: ["data"] };
      const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
      const data = new Blob([excelBuffer], { type: fileType });
      FileSaver.saveAs(data, `Call History Report` + fileExtension);
    }
    await dispatch(
      getCallHistoryAction({
        ...obj,
        pageSize: PAGE_SIZE_50,
        pageIndex: currentPage,
      })
    );
    setExportPopup(true);
    setExportLoad(false);
    setCurrentPage(1);
  };

  const handlePageChange = (event, newPage) => {
    setCurrentPage(newPage);
  };

  const toggle = () => {
    const appliedFilters = getDecryptedItem("callHistoryFilters") || {};
    const appliedFiltersLoc = appliedFilters?.[selectedAccount?._id];
    setState((prev) => ({
      ...prev,
      filterDrawer: !prev.filterDrawer,
      durationFilter: appliedFiltersLoc?.durationFilter || "",
      outComefilter: appliedFiltersLoc?.outcomeFilters || [],
      statusFilter: appliedFiltersLoc?.statusFilter || [],
      min: appliedFiltersLoc?.customFilter
        ? appliedFiltersLoc?.customFilter.split("-")[0] || ""
        : "",
      max: appliedFiltersLoc?.customFilter
        ? appliedFiltersLoc?.customFilter.split("-")[1] || ""
        : "",
      selectedProjectId: appliedFiltersLoc?.selectedProjectId || "",
    }));
    setFilterName(appliedFiltersLoc?.filterName || "");
    setDateRange([
      appliedFiltersLoc?.startDate
        ? new Date(appliedFiltersLoc?.startDate)
        : new Date(currentDate),
      appliedFiltersLoc?.endDate
        ? new Date(appliedFiltersLoc?.endDate)
        : new Date(currentDate),
    ]);
  };

  const exportToggle = () => {
    setExportPopup((prev) => !prev);
  };

  const handlePrevious = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };
  const handleNext = () => {
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const getName = (phone) => {
    const firstName = phone?.customParameters?.firstName || "";
    const lastName = phone?.customParameters?.lastName || "";
    return `${firstName} ${lastName}`.trim() || "No Name";
  };

  const obj = {
    searchText,
    setMainState: setState,
    trackEnabledRecording,
    mainState: state,
    startDate,
    endDate,
    currentDate,
    setDateRange,
    filterName,
    setFilterName,
    setFilterNameFromDropdown,
    setSearchTerm,
    setCurrentPage,
  };

  return (
    <Box sx={{ py: 1, position: "relative" }}>
      <CallHistoryHeader
        setIsReady={setIsReady}
        setSearchText={setSearchText}
        exportToCSV={exportToCSV}
        toggleFilterDrawer={toggle}
        totalCalls={callHistoryDetailsTotalCounts}
        exportLoad={exportLoad}
        setSearchTerm={setSearchTerm}
        searchTerm={searchTerm}
        applyRefresh={applyRefresh}
        loading={loading}
        setCurrentPage={setCurrentPage}
      />
      <DynamicTable
        columns={[
          {
            label: "Phone Number",
            render: ({ campaignRunDetails }) => {
              const callType = campaignRunDetails?.customParameters?.call_type;
              return (
                <Box className="flex-align-center" gap={1.3}>
                  <Tooltip
                    title={getName(campaignRunDetails)}
                    placement="bottom"
                    arrow
                  >
                    <span>{shortenString(campaignRunDetails?.phone, 15)}</span>
                  </Tooltip>
                  {callType && <CallDirectionIcon callType={callType} />}
                </Box>
              );
            },
          },
          {
            label: "Call Status | Ended By",
            render: (row) => {
              const statusData = getCallStatusData(
                row?.campaignRunDetails?.callStatus,
                constantData
              );
              return (
                <Box className="flex-xy-center" gap={1} mr={5}>
                  <StatusButton
                    status={statusData?.status}
                    statusColor={statusData?.statusColor}
                    backgroundColor={statusData?.backgroundColor}
                    statusDescription={statusData?.statusDescription}
                  />
                  <Divider orientation="vertical" flexItem />
                  <Typography className="font-12-mediumgrey">
                    {row?.campaignRunDetails?.botDropped == null ? (
                      "--"
                    ) : row?.campaignRunDetails?.botDropped ? (
                      <Tooltip title="Call Ended by Bot">
                        <Bot color="rgba(0, 0, 0, 0.5)" size={20} />
                      </Tooltip>
                    ) : (
                      <Tooltip title="Call Ended by Human">
                        <User color="rgba(0, 0, 0, 0.5)" size={20} />
                      </Tooltip>
                    )}
                  </Typography>
                </Box>
              );
            },
          },
          {
            label: "Cost",
            render: ({ campaignRunDetails }) =>
              selectedAccount?.params?.pricesWaivers
                ? "--"
                : campaignRunDetails?.newCost || "--",
          },
          {
            label: "UUID",
            render: (row) => row?.campaignRunDetails?.call_uuid,
            styles: { textTransform: "none", padding: 2 },
          },
          {
            label: "Recording",
            render: (row) => (
              <Box className="flex-xy-center">
                <ReactAudioPlayer
                  src={
                    row?.campaignRunDetails?.callRecordingUrl ||
                    `https://storage.googleapis.com/botstream-recordings/${row?.campaignRunDetails?.call_uuid}.mp3`
                  }
                  controls
                  style={{
                    maxWidth: "250px",
                    height: "30px",
                  }}
                />
              </Box>
            ),
          },
          trunk === "show"
            ? {
                label: "Trunk Status | Trunk Status Code",
                render: ({ campaignRunDetails }) => {
                  return (
                    <Box className="flex-xy-center" gap={0.5} mr={10}>
                      <Typography className="font-12-mediumgrey">
                        {campaignRunDetails?.pbxStatusText || "--"}
                      </Typography>
                      <Divider orientation="vertical" flexItem />
                      <Typography className="font-12-mediumgrey">
                        {campaignRunDetails?.pbxStatusCode || "--"}
                      </Typography>
                    </Box>
                  );
                },
              }
            : null,
          {
            label: "Actions",
            render: (row) => (
              <>
                <BaseButton
                  disabled={
                    !["703", "712"].includes(
                      row?.campaignRunDetails?.callStatus
                    )
                  }
                  title="View Transcript"
                  handleClick={() =>
                    openCallTranscriptModal(row?.campaignRunDetails)
                  }
                  styles={{ color: "#4280FF" }}
                />

                <CallHistoryPopover callData={row} />

                <IconButton
                  title="Redirect"
                  onClick={() => handleRedirect(row)}
                >
                  <RedirectIcon />
                </IconButton>
                {selectedAccount?.params?.logAccess?.includes(Stalker) && (
                  <button
                    className="custom-button-icon"
                    title="Logs"
                    onClick={() => {
                      setCall_uuid(row?.campaignRunDetails?.call_uuid);
                      setShow(true);
                    }}
                  >
                    Logs
                  </button>
                )}
              </>
            ),
          },
        ]?.filter(Boolean)}
        data={campaignDataList}
        loading={loading}
        totalPages={totalPages}
        currentPage={currentPage}
        handlePageChange={handlePageChange}
        handlePrevious={handlePrevious}
        handleNext={handleNext}
        noDataAvailableMessage="No Call History Available"
        totalRowCount={callHistoryDetailsTotalCounts}
        rowCount={campaignDataList?.length}
      />

      {showCallTranscriptModal && (
        <CallTranscriptModal
          show={showCallTranscriptModal}
          onClose={closeCallTranscriptModal}
          selectedTranscript={selectedTranscript}
        />
      )}

      {filterDrawer && (
        <Drawer anchor={"right"} open={filterDrawer} onClose={toggle}>
          <FilterBody
            obj={obj}
            encryptionKey="callHistoryFilters"
            showSection={true}
            main={true}
            all={false}
          />
        </Drawer>
      )}
      {call_uuid && (
        <CallLogs
          call_uuid={call_uuid}
          show={show}
          toggle={() => {
            setCall_uuid("");
            setShow(false);
          }}
        />
      )}
      {exportPopup && (
        <CommonPopup
          message={`The selected data are exported successfully`}
          show={exportPopup}
          toggle={exportToggle}
          Icon={ExportFileIcon}
          heading="File exported successfully"
        />
      )}
    </Box>
  );
};

export default CallHistory;
